import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Layout,
  Row,
  Col,
  Card,
  Progress,
  Typography,
  Tag,
  Table,
  Drawer,
  Button,
  Switch,
  Space,
  Spin,
} from "antd";
import {
  HeartOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

import TopBar from "../../components/TopBar";
import AdminSideBar from "../../components/SideBar/AdminSidebar";
import { getCronHealthApi } from "../../services/CronHealth/CronHealthApis";
import "../../styles/HealthDashboard.css";

const { Content } = Layout;
const { Title, Text } = Typography;

type JobStatus = "SUCCESS" | "WARNING" | "FAILED" | "MISSED";

interface JobRow {
  key: string;
  jobKey: string;
  name: string;
  description: string | null;
  endpoint: string | null;
  schedule: string;
  status: JobStatus;
  lastRun: string;
  lastRunIso: string | null;
  duration: string;
  nextRun: string;
  alerts: string[];
  lastError: string | null;
}

interface CriticalIssue {
  key: string;
  jobKey: string;
  type: JobStatus;
  title: string;
  details: { label: string; value: string }[];
}

interface Execution {
  id: number;
  jobKey: string;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  status: JobStatus;
  errorMessage: string | null;
  displayName: string;
}

interface Overview {
  healthScore: number;
  totalJobs: number;
  successfulJobs: number;
  warnings: number;
  failedJobs: number;
  missedJobs: number;
}

const statusColor: Record<JobStatus, string> = {
  SUCCESS: "green",
  WARNING: "gold",
  FAILED: "red",
  MISSED: "volcano",
};

const TREND_LEN = 12;

const formatDuration = (ms: number | null): string => {
  if (ms === null || ms === undefined) return "-";
  if (ms < 1000) return `${ms}ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ${ms % 1000}ms`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Sparkline: React.FC<{ data: number[]; color: string; width?: number; height?: number }> = ({
  data,
  color,
  width = 100,
  height = 32,
}) => {
  if (!data.length) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = data.length > 1 ? width / (data.length - 1) : 0;
  const points = data
    .map((v, i) => `${i * stepX},${height - ((v - min) / range) * height}`)
    .join(" ");
  const lastX = (data.length - 1) * stepX;
  const lastY = height - ((data[data.length - 1] - min) / range) * height;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      style={{ display: "block", width: "100%", height }}
    >
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      <circle cx={lastX} cy={lastY} r={2.5} fill={color} />
    </svg>
  );
};

const StatTile: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number;
  color: string;
  background: string;
  trend: number[];
}> = ({ icon, label, value, color, background, trend }) => (
  <Card style={{ borderRadius: 12, borderTop: `4px solid ${color}` }} bodyStyle={{ padding: 16 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          background,
          color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 12,
            color: "rgba(0,0,0,0.45)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={label}
        >
          {label}
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>{value}</div>
      </div>
    </div>
    <div className="stat-tile-spark" style={{ marginTop: 8 }}>
      <Sparkline data={trend} color={color} width={140} />
    </div>
  </Card>
);

const issueIcon = (status: JobStatus) => {
  switch (status) {
    case "FAILED":
      return <CloseCircleOutlined />;
    case "MISSED":
      return <ExclamationCircleOutlined />;
    case "WARNING":
      return <WarningOutlined />;
    default:
      return <CheckCircleOutlined />;
  }
};

const HealthDashboardPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<Overview>({
    healthScore: 0,
    totalJobs: 0,
    successfulJobs: 0,
    warnings: 0,
    failedJobs: 0,
    missedJobs: 0,
  });
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [issues, setIssues] = useState<CriticalIssue[]>([]);
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [drawerJobKey, setDrawerJobKey] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [trends, setTrends] = useState({
    success: Array(TREND_LEN).fill(0),
    warning: Array(TREND_LEN).fill(0),
    failed: Array(TREND_LEN).fill(0),
    missed: Array(TREND_LEN).fill(0),
  });
  const seededRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const res = await getCronHealthApi();
      if (!res?.success || !res?.data) return;
      const d = res.data;

      setOverview(d.overview);

      setJobs(
        (d.jobs || []).map((j: any) => ({
          key: j.jobKey,
          jobKey: j.jobKey,
          name: j.displayName,
          description: j.description || null,
          endpoint: j.endpoint || null,
          schedule: j.schedule,
          status: j.status as JobStatus,
          lastRun: j.lastRunRelative || formatDateTime(j.lastRun),
          lastRunIso: j.lastRun,
          duration: formatDuration(j.durationMs),
          nextRun: formatDateTime(j.nextRun),
          alerts: j.alerts || [],
          lastError: j.lastError,
        }))
      );

      setIssues(
        (d.criticalIssues || []).map((i: any) => ({
          key: i.jobKey,
          jobKey: i.jobKey,
          type: i.status as JobStatus,
          title: i.displayName,
          details: [
            { label: "Last Run", value: i.lastRunRelative || formatDateTime(i.lastRun) },
            { label: "Next Run", value: formatDateTime(i.nextRun) },
            ...(i.lastError ? [{ label: "Last Error", value: i.lastError }] : []),
          ],
        }))
      );

      setExecutions(d.recentExecutions || []);

      const ov: Overview = d.overview;
      setTrends((prev) => {
        if (!seededRef.current) {
          seededRef.current = true;
          return {
            success: Array(TREND_LEN).fill(ov.successfulJobs),
            warning: Array(TREND_LEN).fill(ov.warnings),
            failed: Array(TREND_LEN).fill(ov.failedJobs),
            missed: Array(TREND_LEN).fill(ov.missedJobs),
          };
        }
        return {
          success: [...prev.success.slice(1), ov.successfulJobs],
          warning: [...prev.warning.slice(1), ov.warnings],
          failed: [...prev.failed.slice(1), ov.failedJobs],
          missed: [...prev.missed.slice(1), ov.missedJobs],
        };
      });

      setLastRefreshed(new Date());
    } catch (err) {
      console.error("Cron health fetch failed", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, 5000);
    return () => clearInterval(id);
  }, [autoRefresh, refresh]);

  const drawerJob = useMemo(
    () => (drawerJobKey ? jobs.find((j) => j.jobKey === drawerJobKey) || null : null),
    [drawerJobKey, jobs]
  );

  const drawerLogs = useMemo(() => {
    if (!drawerJob) return [];
    type LogLine = { ts: number; date: string; time: string; level: "INFO" | "WARN" | "ERROR"; message: string };
    const fmtTime = (d: Date) => d.toLocaleTimeString("en-GB", { hour12: false });
    const fmtDate = (d: Date) =>
      d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    const lines: LogLine[] = [];
    const jobExecs = executions.filter((e) => e.jobKey === drawerJob.jobKey);

    jobExecs.forEach((e) => {
      const startedDate = new Date(e.startedAt);
      const completedDate = e.completedAt ? new Date(e.completedAt) : null;
      if (e.status === "SUCCESS") {
        lines.push({
          ts: startedDate.getTime(),
          date: fmtDate(startedDate),
          time: fmtTime(startedDate),
          level: "INFO",
          message: `${drawerJob.name} started`,
        });
        if (completedDate) {
          lines.push({
            ts: completedDate.getTime(),
            date: fmtDate(completedDate),
            time: fmtTime(completedDate),
            level: "INFO",
            message: `${drawerJob.name} completed in ${formatDuration(e.durationMs)}`,
          });
        }
      } else if (e.status === "FAILED") {
        lines.push({
          ts: startedDate.getTime(),
          date: fmtDate(startedDate),
          time: fmtTime(startedDate),
          level: "INFO",
          message: `${drawerJob.name} started`,
        });
        const errDate = completedDate || startedDate;
        lines.push({
          ts: errDate.getTime(),
          date: fmtDate(errDate),
          time: fmtTime(errDate),
          level: "ERROR",
          message: `${drawerJob.name} failed: ${e.errorMessage || "unknown error"}`,
        });
      } else {
        lines.push({
          ts: startedDate.getTime(),
          date: fmtDate(startedDate),
          time: fmtTime(startedDate),
          level: "WARN",
          message: `${drawerJob.name} ${e.status.toLowerCase()}`,
        });
      }
    });

    const now = new Date();
    drawerJob.alerts.forEach((a) => {
      lines.push({
        ts: now.getTime(),
        date: fmtDate(now),
        time: fmtTime(now),
        level: drawerJob.status === "FAILED" ? "ERROR" : "WARN",
        message: a,
      });
    });

    if (drawerJob.lastError) {
      const errDate = drawerJob.lastRunIso ? new Date(drawerJob.lastRunIso) : now;
      lines.push({
        ts: errDate.getTime(),
        date: fmtDate(errDate),
        time: fmtTime(errDate),
        level: "ERROR",
        message: drawerJob.lastError,
      });
    }

    if (!lines.length) {
      lines.push({
        ts: now.getTime(),
        date: fmtDate(now),
        time: fmtTime(now),
        level: drawerJob.status === "MISSED" ? "WARN" : "INFO",
        message: `No recent executions for ${drawerJob.name}`,
      });
    }

    const byDate = new Map<
      string,
      { date: string; ts: number; lines: (LogLine & { _idx: number })[] }
    >();
    lines.forEach((l, idx) => {
      let g = byDate.get(l.date);
      if (!g) {
        g = { date: l.date, ts: l.ts, lines: [] };
        byDate.set(l.date, g);
      }
      g.ts = Math.max(g.ts, l.ts);
      g.lines.push({ ...l, _idx: idx });
    });

    const groups = Array.from(byDate.values())
      .sort((a, b) => b.ts - a.ts)
      .map((g) => ({
        date: g.date,
        lines: g.lines.slice().sort((a, b) => a.ts - b.ts || a._idx - b._idx),
      }));
    return groups;
  }, [drawerJob, executions]);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (v: string) => <strong>{v}</strong>,
    },
    {
      title: "Schedule",
      dataIndex: "schedule",
      key: "schedule",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: JobStatus) => <Tag color={statusColor[v]}>{v}</Tag>,
    },
    { title: "Last Run", dataIndex: "lastRun", key: "lastRun" },
    { title: "Duration", dataIndex: "duration", key: "duration" },
    { title: "Next Run", dataIndex: "nextRun", key: "nextRun" },
  ];

  const healthStroke =
    overview.healthScore >= 80
      ? { "0%": "#52c41a", "100%": "#13c2c2" }
      : overview.healthScore >= 50
      ? { "0%": "#faad14", "100%": "#fa8c16" }
      : { "0%": "#ff4d4f", "100%": "#fa541c" };

  const healthLabel =
    overview.healthScore >= 80
      ? "System operating normally"
      : overview.healthScore >= 50
      ? "Degraded performance"
      : "Critical — action required";

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <AdminSideBar />
      <Layout className="site-layout">
        <TopBar />
        <Content>
          <div style={{ padding: 24, background: "#fff", minHeight: "100%" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
                flexWrap: "wrap",
                marginBottom: 16,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <HeartOutlined style={{ color: "#f04f05", fontSize: 22 }} />
                <Title level={3} style={{ margin: 0 }}>Cron Health</Title>
              </div>
              <Space size="middle">
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Last refreshed {lastRefreshed.toLocaleTimeString()}
                </Text>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Text style={{ fontSize: 12 }}>Auto-refresh</Text>
                  <Switch size="small" checked={autoRefresh} onChange={setAutoRefresh} />
                </span>
                <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>
                  Refresh
                </Button>
              </Space>
            </div>

            <Spin spinning={loading && !seededRef.current}>
              <Card style={{ borderRadius: 12, marginBottom: 24 }} bodyStyle={{ padding: 24 }}>
                <Row gutter={[24, 24]} align="middle">
                  <Col xs={24} md={8}>
                    <Text type="secondary">Overall Health</Text>
                    <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 8 }}>
                      <Progress
                        type="circle"
                        percent={overview.healthScore}
                        width={96}
                        strokeColor={healthStroke}
                      />
                      <div>
                        <div style={{ fontSize: 32, fontWeight: 700, lineHeight: 1 }}>
                          {overview.healthScore}%
                        </div>
                        <Text type="secondary">{healthLabel}</Text>
                        <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4 }}>
                          {overview.totalJobs} jobs tracked
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} md={16}>
                    <Row gutter={[16, 16]}>
                      <Col xs={12} sm={6}>
                        <StatTile
                          icon={<CheckCircleOutlined />}
                          label="Successful Jobs"
                          value={overview.successfulJobs}
                          color="#52c41a"
                          background="#f6ffed"
                          trend={trends.success}
                        />
                      </Col>
                      <Col xs={12} sm={6}>
                        <StatTile
                          icon={<WarningOutlined />}
                          label="Warnings"
                          value={overview.warnings}
                          color="#faad14"
                          background="#fffbe6"
                          trend={trends.warning}
                        />
                      </Col>
                      <Col xs={12} sm={6}>
                        <StatTile
                          icon={<CloseCircleOutlined />}
                          label="Failed Jobs"
                          value={overview.failedJobs}
                          color="#ff4d4f"
                          background="#fff1f0"
                          trend={trends.failed}
                        />
                      </Col>
                      <Col xs={12} sm={6}>
                        <StatTile
                          icon={<ExclamationCircleOutlined />}
                          label="Missed Jobs"
                          value={overview.missedJobs}
                          color="#fa541c"
                          background="#fff2e8"
                          trend={trends.missed}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Card>

              {issues.length > 0 && (
                <>
                  <Title
                    level={5}
                    style={{
                      textTransform: "uppercase",
                      color: "#8c8c8c",
                      fontSize: 12,
                      letterSpacing: 0.6,
                      marginBottom: 12,
                    }}
                  >
                    Critical Issues
                  </Title>
                  <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    {issues.map((issue) => {
                      const borderColor =
                        issue.type === "FAILED" ? "#ff4d4f" : issue.type === "MISSED" ? "#fa541c" : "#faad14";
                      return (
                        <Col xs={24} md={12} key={issue.key}>
                          <Card
                            hoverable
                            style={{ borderRadius: 12, borderLeft: `4px solid ${borderColor}` }}
                            bodyStyle={{ padding: 20 }}
                            onClick={() => {
                              const job = jobs.find((j) => j.jobKey === issue.jobKey);
                              if (job) setDrawerJobKey(job.jobKey);
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                              <Tag color={statusColor[issue.type]} style={{ margin: 0 }}>
                                {issueIcon(issue.type)} {issue.type}
                              </Tag>
                              <strong style={{ fontSize: 15 }}>{issue.title}</strong>
                            </div>
                            {issue.details.map((d) => (
                              <div key={d.label} style={{ fontSize: 13, marginTop: 4 }}>
                                <Text type="secondary">{d.label}: </Text>
                                <span>{d.value}</span>
                              </div>
                            ))}
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                </>
              )}

              <Title
                level={5}
                style={{
                  textTransform: "uppercase",
                  color: "#8c8c8c",
                  fontSize: 12,
                  letterSpacing: 0.6,
                  marginBottom: 12,
                }}
              >
                Job List
              </Title>
              <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 0 }}>
                <Table
                  dataSource={jobs}
                  columns={columns}
                  pagination={false}
                  size="middle"
                  onRow={(record) => ({
                    onClick: () => setDrawerJobKey(record.jobKey),
                    style: { cursor: "pointer" },
                  })}
                />
              </Card>

              <Title
                level={5}
                style={{
                  textTransform: "uppercase",
                  color: "#8c8c8c",
                  fontSize: 12,
                  letterSpacing: 0.6,
                  margin: "24px 0 12px",
                }}
              >
                Recent Executions
              </Title>
              <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 0 }}>
                <Table
                  dataSource={executions}
                  rowKey="id"
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                  size="middle"
                  onRow={(record) => ({
                    onClick: () => setDrawerJobKey(record.jobKey),
                    style: { cursor: "pointer" },
                  })}
                  columns={[
                    {
                      title: "Job",
                      dataIndex: "displayName",
                      key: "displayName",
                      render: (v: string) => <strong>{v}</strong>,
                    },
                    {
                      title: "Status",
                      dataIndex: "status",
                      key: "status",
                      render: (v: JobStatus) => <Tag color={statusColor[v]}>{v}</Tag>,
                    },
                    {
                      title: "Started",
                      dataIndex: "startedAt",
                      key: "startedAt",
                      render: (v: string) => formatDateTime(v),
                    },
                    {
                      title: "Completed",
                      dataIndex: "completedAt",
                      key: "completedAt",
                      render: (v: string | null) => formatDateTime(v),
                    },
                    {
                      title: "Duration",
                      dataIndex: "durationMs",
                      key: "durationMs",
                      render: (v: number | null) => formatDuration(v),
                    },
                    {
                      title: "Error",
                      dataIndex: "errorMessage",
                      key: "errorMessage",
                      render: (v: string | null) =>
                        v ? <Text type="danger">{v}</Text> : <Text type="secondary">—</Text>,
                    },
                  ]}
                />
              </Card>
            </Spin>
          </div>

          <Drawer
            title={drawerJob ? `${drawerJob.name} — logs` : ""}
            placement="right"
            width={520}
            open={!!drawerJob}
            onClose={() => setDrawerJobKey(null)}
            bodyStyle={{ padding: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}
          >
            {drawerJob && (
              <>
                <div style={{ padding: "16px 24px", borderBottom: "1px solid #f0f0f0", flexShrink: 0 }}>
                  {drawerJob.endpoint && (
                    <div style={{ marginBottom: 12 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Endpoint</Text>
                      <div
                        style={{
                          marginTop: 4,
                          padding: "4px 10px",
                          background: "#f5f5f5",
                          borderRadius: 6,
                          fontFamily: "Menlo, Consolas, monospace",
                          fontSize: 12,
                          color: "#1677ff",
                          wordBreak: "break-all",
                        }}
                      >
                        {drawerJob.endpoint}
                      </div>
                    </div>
                  )}
                  {drawerJob.description && (
                    <div style={{ marginBottom: 12, color: "rgba(0,0,0,0.65)", fontSize: 13 }}>
                      {drawerJob.description}
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <Tag color={statusColor[drawerJob.status]}>{drawerJob.status}</Tag>
                    <Text type="secondary">Schedule: {drawerJob.schedule}</Text>
                    <Text type="secondary">Last run: {drawerJob.lastRun}</Text>
                    <Text type="secondary">Duration: {drawerJob.duration}</Text>
                    <Text type="secondary">Next: {drawerJob.nextRun}</Text>
                  </div>
                </div>
                <div style={{ flex: 1, overflow: "auto", padding: "16px 24px" }}>
                  {drawerLogs.map((group) => (
                    <div key={group.date} style={{ marginBottom: 12 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#595959",
                          textTransform: "uppercase",
                          letterSpacing: 0.6,
                          marginBottom: 6,
                        }}
                      >
                        {group.date}
                      </div>
                      <div
                        style={{
                          background: "#0f1115",
                          color: "#d4d4d4",
                          borderRadius: 8,
                          padding: 12,
                          fontFamily: "Menlo, Consolas, monospace",
                          fontSize: 12,
                          lineHeight: 1.6,
                        }}
                      >
                        {group.lines.map((l, i) => {
                          const lvlColor =
                            l.level === "ERROR" ? "#ff7875" : l.level === "WARN" ? "#ffd666" : "#95de64";
                          return (
                            <div key={i} style={{ display: "flex", gap: 8 }}>
                              <span style={{ color: "#8c8c8c", flex: "0 0 64px" }}>{l.time}</span>
                              <span
                                style={{
                                  color: lvlColor,
                                  fontWeight: 600,
                                  flex: "0 0 48px",
                                }}
                              >
                                {l.level}
                              </span>
                              <span style={{ flex: 1, minWidth: 0, wordBreak: "break-all", whiteSpace: "pre-wrap" }}>{l.message}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Drawer>
        </Content>
      </Layout>
    </Layout>
  );
};

export default HealthDashboardPage;
